import React from 'react';
import { useParams } from 'react-router-dom';
import { useQueries } from 'react-query';
import * as Cesium from 'cesium';
import { Clock } from 'resium';
import API from '@wheeebo/polaris-api';
import { useCesiumMap } from '@wheeebo/polaris-ui';
import CesiumMap from './CesiumMap';
import FlightPathEntity from './FlightPathEntity';
import WaypointEntity from './WaypointEntity';
const INITIAL_ITEM_COUNT = 25;
const NO_FLY_ZONE_SEARCH_RADIUS_METER = 10000;
function SimulationPanel({ env }) {
    const [page] = React.useState(0);
    const { center } = useCesiumMap();
    const params = useParams();
    const [start, setStart] = React.useState();
    const [stop, setStop] = React.useState();
    const [trackedFlightPathId, setTrackedFlightPathId] = React.useState();
    const missionOption = { preload: ['geo_fences', 'safe_ports'] };
    const flightPlanOptions = {
        limit: INITIAL_ITEM_COUNT,
        offset: page * INITIAL_ITEM_COUNT,
        missionId: params.missionId,
        preload: ['operator', 'vehicle', 'flight_path', 'waypoints'],
    };
    const noFlyZoneOptions = {
        latitude: center?.lat || 0,
        longitude: center?.lng || 0,
        radius: NO_FLY_ZONE_SEARCH_RADIUS_METER,
    };
    const [{ data: mission }, { data: flightPlans }, { data: noFlyZones }] = useQueries([
        {
            queryKey: params.missionId ? API.Missions.getKey(params.missionId, missionOption) : '',
            queryFn: () => (params.missionId ? API.Missions.get(params.missionId, missionOption) : undefined),
        },
        {
            queryKey: API.FlightPlans.listKey(flightPlanOptions),
            queryFn: () => API.FlightPlans.list(flightPlanOptions),
        },
        {
            queryKey: API.NoFlyZones.listKey(noFlyZoneOptions),
            queryFn: () => API.NoFlyZones.list(noFlyZoneOptions),
            enabled: !!center,
        },
    ]);
    React.useEffect(() => {
        if (!flightPlans?.items?.length) {
            return;
        }
        const first = flightPlans?.items?.map((e) => e.flightPath)?.at(0);
        if (!first) {
            return;
        }
        setTrackedFlightPathId(first.id);
        const startAt = flightPlans?.items
            ?.map((e) => e.startAt)
            .reduce((prev, curr) => Math.min(prev, curr), Number.MAX_VALUE);
        const endAt = flightPlans?.items?.map((e) => e.endAt).reduce((prev, curr) => Math.max(prev, curr), 0);
        setStart(Cesium.JulianDate.fromDate(new Date(startAt)));
        setStop(Cesium.JulianDate.fromDate(new Date(endAt)));
    }, [flightPlans]);
    return (React.createElement(CesiumMap, { env: env, mission: mission, geoFences: mission?.geoFences, safePorts: mission?.safePorts, noFlyZones: noFlyZones?.items }, start && stop ? (React.createElement(React.Fragment, null,
        React.createElement(Clock, { startTime: start?.clone(), stopTime: stop?.clone(), currentTime: start?.clone(), clockRange: Cesium.ClockRange.LOOP_STOP }),
        flightPlans?.items?.map((flightPlan) => {
            const flightPath = flightPlan.flightPath;
            return (React.createElement(FlightPathEntity, { key: flightPath.id, flightPlan: flightPlan, flightPath: flightPath, tracked: flightPath.id === trackedFlightPathId, start: start, stop: stop }));
        }),
        flightPlans?.items
            ?.map((flightPlan) => flightPlan.flightPath)
            ?.flatMap((flightPath) => flightPath.waypoints?.map((waypoint, index) => (React.createElement(WaypointEntity, { key: waypoint.id, hideMarker: index === 0, flightPath: flightPath, waypoint: waypoint, tracked: flightPath.id === trackedFlightPathId })))))) : undefined));
}
export default React.memo(SimulationPanel);
